import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import { useAuth } from "hooks/useAuth";
import {
  PUBLIC_COMPUTER_MODE_ENABLED,
  isPublicComputer,
} from "services/device/publicComputerMode";

export const PUBLIC_COMPUTER_IDLE_MS = 10 * 60 * 1000;
export const PUBLIC_COMPUTER_COUNTDOWN_MS = 60 * 1000;

const ACTIVITY_EVENTS = ["pointerdown", "keydown", "touchstart"];

/**
 * Idle sign-out for public-computer patient sessions.
 * Active only when PUBLIC_COMPUTER_MODE_ENABLED and isPublicComputer().
 *
 * @param {{ idleMs?: number, countdownMs?: number }} [options]
 */
export function usePublicComputerIdleSignOut({
  idleMs = PUBLIC_COMPUTER_IDLE_MS,
  countdownMs = PUBLIC_COMPUTER_COUNTDOWN_MS,
} = {}) {
  const { logout } = useAuth();
  const [countdownOpen, setCountdownOpen] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(
    Math.ceil(countdownMs / 1000),
  );
  const idleTimerRef = useRef(null);
  const countdownTimerRef = useRef(null);
  const countdownTickRef = useRef(null);
  const logoutRef = useRef(logout);
  const countdownOpenRef = useRef(false);

  useEffect(() => {
    logoutRef.current = logout;
  }, [logout]);

  useEffect(() => {
    countdownOpenRef.current = countdownOpen;
  }, [countdownOpen]);

  const clearIdleTimer = useCallback(() => {
    if (idleTimerRef.current != null) {
      window.clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
  }, []);

  const clearCountdownTimers = useCallback(() => {
    if (countdownTimerRef.current != null) {
      window.clearTimeout(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
    if (countdownTickRef.current != null) {
      window.clearInterval(countdownTickRef.current);
      countdownTickRef.current = null;
    }
  }, []);

  const startIdleTimer = useCallback(() => {
    clearIdleTimer();
    if (!PUBLIC_COMPUTER_MODE_ENABLED || !isPublicComputer()) return;
    idleTimerRef.current = window.setTimeout(() => {
      setSecondsLeft(Math.ceil(countdownMs / 1000));
      setCountdownOpen(true);
    }, idleMs);
  }, [clearIdleTimer, countdownMs, idleMs]);

  const staySignedIn = useCallback(() => {
    clearCountdownTimers();
    setCountdownOpen(false);
    setSecondsLeft(Math.ceil(countdownMs / 1000));
    startIdleTimer();
  }, [clearCountdownTimers, countdownMs, startIdleTimer]);

  useEffect(() => {
    if (!PUBLIC_COMPUTER_MODE_ENABLED || !isPublicComputer()) {
      clearIdleTimer();
      clearCountdownTimers();
      setCountdownOpen(false);
      return undefined;
    }

    const onActivity = () => {
      // Activity during the countdown does not dismiss it — only Stay signed in.
      if (countdownOpenRef.current) return;
      startIdleTimer();
    };

    startIdleTimer();
    for (const eventName of ACTIVITY_EVENTS) {
      window.addEventListener(eventName, onActivity, { passive: true });
    }

    return () => {
      clearIdleTimer();
      clearCountdownTimers();
      for (const eventName of ACTIVITY_EVENTS) {
        window.removeEventListener(eventName, onActivity);
      }
    };
  }, [clearCountdownTimers, clearIdleTimer, startIdleTimer]);

  useEffect(() => {
    if (!countdownOpen) {
      clearCountdownTimers();
      return undefined;
    }

    const totalSeconds = Math.ceil(countdownMs / 1000);
    setSecondsLeft(totalSeconds);

    countdownTickRef.current = window.setInterval(() => {
      setSecondsLeft((prev) => Math.max(0, prev - 1));
    }, 1000);

    countdownTimerRef.current = window.setTimeout(() => {
      clearCountdownTimers();
      setCountdownOpen(false);
      Promise.resolve(logoutRef.current?.()).catch(() => {});
    }, countdownMs);

    return () => {
      clearCountdownTimers();
    };
  }, [clearCountdownTimers, countdownMs, countdownOpen]);

  return {
    countdownOpen,
    secondsLeft,
    staySignedIn,
  };
}

/**
 * Mount on patient-facing shells/routes only.
 * @param {{ idleMs?: number, countdownMs?: number }} [props]
 */
export function PublicComputerIdleGuard({ idleMs, countdownMs } = {}) {
  const { countdownOpen, secondsLeft, staySignedIn } =
    usePublicComputerIdleSignOut({ idleMs, countdownMs });

  return (
    <Dialog open={countdownOpen} onClose={staySignedIn}>
      <DialogTitle>Still there?</DialogTitle>
      <DialogContent>
        <DialogContentText>
          You&apos;ll be signed out to protect your information.
          {secondsLeft > 0 ? ` (${secondsLeft}s)` : ""}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={staySignedIn} variant="contained" autoFocus>
          Stay signed in
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default PublicComputerIdleGuard;
